import React, { useState, useEffect } from 'react';
import { CancelToken } from 'axios';
import { useSnackbar } from 'notistack';
import { CircularProgressCentered } from '../../../circular-progress-centered/circular-progress-centered';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { useApi, useApiOnRender, useErrorSnackbar } from '../../../../hooks';
import { AssignResponsibleCaseworkerResponse, AssignResponsibleCaseworkerRequest, GetUserByIdResponse, GetUserByIdRequest } from '../../../../api-service/clients';
import { AssignCaseworkerDialogComponent } from './assign-caseworker-dialog-component';
import { useAuthenticatedUser } from '../../../../authentication';

export interface IAssignCaseworkerDialogProps {
  offerIds: string[];
}

export function AssignCaseworkerOfferDialog({ offerIds }: IAssignCaseworkerDialogProps) {
  const [userId, setUserId] = useState<string | undefined>();
  const [teamId, setTeamId] = useState<string | undefined>();
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const { enqueueSnackbar } = useSnackbar();

  const authenticatedUser = useAuthenticatedUser();

  const offerAndActivityClient = moduleClientFactory.createOfferAndActivityClient();
  const organizationClient = moduleClientFactory.createOrganizationClient();

  const currentUserApi = useApiOnRender(cancelToken => organizationClient.getUserById({ userId: authenticatedUser?.profile.id as string }, cancelToken), {} as GetUserByIdResponse);
  const assignCaseworkerApi = useApi(
    (cancelToken: CancelToken, request: AssignResponsibleCaseworkerRequest) => offerAndActivityClient.assignResponsibleCaseworker(request, cancelToken),
    {} as AssignResponsibleCaseworkerResponse
  );

  const getUserByIdApi = useApi((cancelToken, request: GetUserByIdRequest) => organizationClient.getUserById(request, cancelToken), {} as GetUserByIdResponse);

  useErrorSnackbar(currentUserApi.error, assignCaseworkerApi.error);

  useEffect(() => {
    if (currentUserApi.isReady) {
      setUserId(currentUserApi.result.user?.id);
      setTeamId(currentUserApi.result.user?.teamId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserApi.isReady]);

  const handleConfirm = async () => {
    // Use offerIds from selected rows of table if gridApiRef is defined (ie. from offer table instead of worklist), else use the offerIds set from the bulk action worklist
    const request: AssignResponsibleCaseworkerRequest = {
      responsibleTeamId: teamId ?? '',
      offerIds,
      responsibleUserId: userId,
    };

    const resp = await assignCaseworkerApi.invoke(request);
    if (resp.result.isValid) {
      enqueueSnackbar('Ansvarlig sagsbehandler og team blev tildelt', { variant: 'success' });
    }

    setIsOpen(false);
  };

  const handleTeamChange = (value: string | undefined) => {
    setTeamId(value);
    setUserId(undefined);
  };

  const handleUserChange = async (selectedUserId: string | undefined) => {
    setUserId(selectedUserId);

    if (selectedUserId) {
      const response = await getUserByIdApi.invoke({ userId: selectedUserId });

      if (response.result) {
        const { user } = response.result;
        setTeamId(user?.teamId);
      }
    } else {
      setTeamId(currentUserApi.result.user?.teamId);
    }
  };
  function handleClose() {
    setIsOpen(false);
  }
  return currentUserApi.isReady ? (
    <AssignCaseworkerDialogComponent
      onConfirm={handleConfirm}
      onTeamChange={handleTeamChange}
      onUserChange={handleUserChange}
      teamId={teamId}
      userId={userId}
      isOpen={isOpen}
      onClose={handleClose}
    />
  ) : (
    <CircularProgressCentered />
  );
}
